import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  getUserAccessibleProjects 
} from '../utils/userPermissions';
import { 
  Search, 
  Target, 
  X, 
  Eye, 
  CheckCircle, 
  Clock, 
  Plus
} from 'lucide-react';
import { api } from '../services/api';

const Matching = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Load live data from server Neo4j database & localStorage
  const [projects, setProjects] = useState(() => JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));
  const [people, setPeople] = useState(() => JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));

  useEffect(() => {
    api.getProjects().then(data => {
      if (Array.isArray(data) && data.length > 0) setProjects(data);
    });
    api.getPeople().then(data => {
      if (Array.isArray(data) && data.length > 0) setPeople(data);
    });

    const handleStorage = () => {
      setProjects(JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));
      setPeople(JSON.parse(localStorage.getItem('skillmesh_people') || '[]'));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const userProjects = getUserAccessibleProjects(projects, currentUser, isAdmin);

  // Compute matches between accessible projects and talent
  const computedMatches = [];
  let matchId = 1;

  userProjects.forEach(proj => {
    const reqSkills = (proj.requiredSkills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());
    if (reqSkills.length === 0) return;

    people.forEach(person => {
      const pSkills = (person.skills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());
      const overlapping = pSkills.filter(s => reqSkills.includes(s));
      
      if (overlapping.length > 0) {
        const skillsMatchPct = Math.min(100, Math.round((overlapping.length / reqSkills.length) * 100));
        const matchScore = Math.min(100, Math.round(skillsMatchPct * 0.8 + (person.rating || 4.5) * 4));

        computedMatches.push({
          id: matchId++,
          person: {
            id: person.id,
            name: person.name,
            role: person.role,
            experience: person.experience,
            skills: person.skills || [],
            rating: person.rating || 4.5,
            availability: person.availability || 'Available'
          },
          project: {
            id: proj.id,
            name: proj.name,
            status: proj.status || 'In Progress',
            requiredSkills: proj.requiredSkills || [],
            deadline: proj.endDate || 'Active'
          },
          overlappingSkills: overlapping,
          skillsMatch: skillsMatchPct,
          matchScore: matchScore,
          status: skillsMatchPct >= 75 ? 'Matched' : 'Pending'
        });
      }
    });
  });

  // Filter matches
  const filteredMatches = computedMatches.filter(match => {
    const matchesSearch = (match.person.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (match.project.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || match.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Matched':
        return isDarkMode
          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
          : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'Pending':
        return isDarkMode
          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
          : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold';
      case 'Completed':
        return isDarkMode
          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
          : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Rejected':
        return isDarkMode
          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
          : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
      default:
        return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const openPersonDrawer = (person) => {
    setSelectedPerson(person);
    setIsDrawerOpen(true);
  };

  const closePersonDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedPerson(null);
  };

  return (
    <div className={`space-y-6 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center">
            <h1 className="text-3xl font-bold">Matching</h1>
            {isAdmin ? (
              <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30">
                Admin: All Matches ({computedMatches.length})
              </span>
            ) : (
              <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                Matches for My Projects ({computedMatches.length})
              </span>
            )}
          </div>
          <p className={`mt-1 transition-colors duration-200 ${
            isDarkMode ? 'text-dark-300' : 'text-light-600'
          }`}>
            {isAdmin
              ? 'Find and analyze matching candidates across all projects in the organization.'
              : 'Skill overlap candidates specifically matching your published projects.'}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
            isDarkMode ? 'text-dark-400' : 'text-light-500'
          }`} />
          <input
            type="text"
            placeholder="Search by candidate name or project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`input-field w-full pl-10 transition-all duration-200 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          />
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={`input-field transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}
        >
          <option value="all">All Status</option>
          <option value="Matched">Matched</option>
          <option value="Pending">Pending</option>
        </select>
      </div>

      {/* Matches Table */}
      <div className={`card transition-all duration-200 ${
        isDarkMode ? 'dark' : 'light'
      }`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b transition-colors duration-200 ${
                isDarkMode ? 'border-dark-800' : 'border-light-200'
              }`}>
                <th className="text-left py-4 px-6 font-semibold">Candidate</th>
                <th className="text-left py-4 px-6 font-semibold">Your Project</th>
                <th className="text-left py-4 px-6 font-semibold">Skill Match</th>
                <th className="text-left py-4 px-6 font-semibold">Match Score</th>
                <th className="text-left py-4 px-6 font-semibold">Status</th>
                <th className="text-right py-4 px-6 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatches.map((match) => (
                <tr key={match.id} className={`border-b transition-colors duration-200 ${
                  isDarkMode ? 'border-dark-800 hover:bg-dark-800/50' : 'border-light-200 hover:bg-light-100/50'
                }`}>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {match.person.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{match.person.name}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-dark-400' : 'text-light-500'}`}>
                          {match.person.role}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="font-semibold text-sm">{match.project.name}</div>
                    <div className="text-xs text-indigo-400">
                      {match.overlappingSkills?.slice(0, 2).join(', ')}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <div className={`w-16 rounded-full h-2 ${isDarkMode ? 'bg-dark-800' : 'bg-gray-200'}`}>
                        <div
                          className="bg-emerald-500 h-2 rounded-full"
                          style={{ width: `${match.skillsMatch}%` }}
                        />
                      </div>
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{match.skillsMatch}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <div className={`w-16 rounded-full h-2 ${isDarkMode ? 'bg-dark-800' : 'bg-gray-200'}`}>
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${match.matchScore}%` }}
                        />
                      </div>
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>{match.matchScore}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(match.status)}`}>
                      {match.status === 'Matched' ? (
                        <CheckCircle className={`h-3.5 w-3.5 mr-1 flex-shrink-0 ${isDarkMode ? 'text-green-400' : 'text-emerald-700'}`} />
                      ) : (
                        <Clock className={`h-3.5 w-3.5 mr-1 flex-shrink-0 ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`} />
                      )}
                      <span>{match.status}</span>
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openPersonDrawer(match.person)}
                        className="p-1.5 rounded-lg hover:bg-dark-700 text-gray-400 hover:text-white"
                        title="Quick View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <Link
                        to={`/people/${match.person.id}`}
                        className="btn-secondary text-xs py-1 px-2.5"
                      >
                        Profile
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMatches.length === 0 && (
          <div className="text-center py-12 px-6">
            {userProjects.length === 0 ? (
              <div className="max-w-md mx-auto space-y-3">
                <Target className="h-12 w-12 text-blue-400 mx-auto" />
                <h3 className="text-lg font-bold">No Projects to Match</h3>
                <p className="text-sm text-gray-400">
                  Publish your project with required skills to activate intelligent talent matching.
                </p>
                <Link to="/projects/new" className="btn-primary inline-flex items-center text-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Publish Project
                </Link>
              </div>
            ) : (
              <div className="text-gray-400 text-sm">
                No candidate matches found matching your filters.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Person Detail Drawer */}
      {isDrawerOpen && selectedPerson && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black/60" onClick={closePersonDrawer} />
          <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
            <div className={`w-screen max-w-md p-6 ${isDarkMode ? 'bg-dark-900 text-white' : 'bg-white text-gray-900'} shadow-xl space-y-6`}>
              <div className="flex items-center justify-between pb-4 border-b border-gray-700">
                <h3 className="text-lg font-bold">Candidate Preview</h3>
                <button onClick={closePersonDrawer} className="p-1 rounded-lg hover:bg-gray-800">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-3">
                  {selectedPerson.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <h4 className="text-xl font-bold">{selectedPerson.name}</h4>
                <p className="text-sm text-gray-400">{selectedPerson.role}</p>
              </div>

              <div>
                <h5 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Skills</h5>
                <div className="flex flex-wrap gap-1.5">
                  {selectedPerson.skills.map(s => (
                    <span key={s} className="px-2 py-0.5 rounded bg-dark-800 text-xs text-white">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800 flex space-x-3">
                <Link
                  to={`/people/${selectedPerson.id}`}
                  className="btn-primary flex-1 text-center"
                >
                  View Full Profile
                </Link>
                <button onClick={closePersonDrawer} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Matching;
